/**
 * Email Verification Gate
 *
 * Registers itself as the handler for the api.ts response interceptor's
 * EMAIL_NOT_VERIFIED case, and shows a blocking modal with a resend button
 * when a mutating request (model upload, embed creation) is rejected for
 * an unverified email. Mounted once at the app root.
 */

import React, { useEffect, useState } from 'react';
import { setEmailNotVerifiedHandler } from '../../services/api';
import { authService } from '../../services/auth.service';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import Button from '../common/Button';

const EmailVerificationGate: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    setEmailNotVerifiedHandler(() => setOpen(true));
  }, []);

  const handleResend = async () => {
    setResending(true);
    try {
      await authService.resendVerification();
      showToast('success', 'Verification email sent — check your inbox.');
    } catch {
      showToast('error', 'Could not resend the verification email. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)} title="Verify your email">
      <p className="text-sm text-gray-600">
        You need to confirm your email address before you can upload models or create embeds.
      </p>
      <div className="mt-4 flex justify-end gap-3">
        <Button variant="secondary" onClick={() => setOpen(false)}>
          Close
        </Button>
        <Button onClick={handleResend} loading={resending} disabled={resending}>
          Resend verification email
        </Button>
      </div>
    </Modal>
  );
};

export default EmailVerificationGate;
